/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import { fetchDepartmentTreeV2 } from '@coze-foundation/account-adapter';

import { collectDeptNodesByIds } from '../utils/collect-dept-nodes';
import { PERMISSION_SPECIFIC_ORG } from '../constants/permission-scope';
import {
  createDefaultPublishScopeValue,
  publishScopeValueFromConfig,
  type PublishScopeValue,
} from '../components/app-publish/publish-scope-selector';
import { getPublishConfig } from '../api/published-apps';

async function loadDeptOrgsForEcho(scopeDeptIds: string[]) {
  const tree = await fetchDepartmentTreeV2();
  return collectDeptNodesByIds(Array.isArray(tree) ? tree : [], scopeDeptIds);
}

export function usePublishScopeConfig(botId?: string) {
  const [scopeValue, setScopeValue] = useState<PublishScopeValue>(
    createDefaultPublishScopeValue,
  );
  const [loading, setLoading] = useState(Boolean(botId));

  useEffect(() => {
    if (!botId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const config = await getPublishConfig(botId);
        if (!config.found || cancelled) {
          return;
        }

        let orgs: ReturnType<typeof collectDeptNodesByIds> = [];
        if (
          config.scope_type === PERMISSION_SPECIFIC_ORG &&
          config.scope_dept_ids.length > 0
        ) {
          orgs = await loadDeptOrgsForEcho(config.scope_dept_ids).catch(
            () => [],
          );
        }

        if (!cancelled) {
          setScopeValue(
            publishScopeValueFromConfig(
              config.scope_type,
              config.scope_dept_ids,
              orgs,
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [botId]);

  return { scopeValue, setScopeValue, loading };
}
